import {generateSqlToHumanFriendlyMonthsBetweenRange} from "~/utilities/analytics/generateSqlToHumanFriendlyMonthsBetweenRange";
import {DateTime} from "luxon";
import type {HadesRawData} from "~/components/reports/widgetSpecific/maps/widgetTypeRawDataToTransformedData";
import {Granularity} from "~/modules/reports/domain/reportTypes";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";
import {generateDatesBetweenRange} from "~/utilities/utilities";

export function generateHadesDataForAllDatesBasedOnGranularity(
    data: HadesRawData,
    dateColumnIndex: number,
    startDate: Iso8601DateTime,
    endDate: Iso8601DateTime,
    granularity: Granularity,
): HadesRawData {
    const dataWithAllDates: HadesRawData = {
        columnMetadata: data.columnMetadata,
        dataPoints: [],
    };

    const metadataLength = Object.entries(data.columnMetadata).length;

    switch (granularity) {
        case Granularity.day: {
            const allDatesBetweenDateRange = generateDatesBetweenRange(
                startDate,
                endDate,
            );

            dataWithAllDates.dataPoints = allDatesBetweenDateRange.map(
                (date) => {
                    const dataWithCurrentDate = data.dataPoints.find(
                        (dataPoint) => {
                            const currentDate =
                                dataPoint[dateColumnIndex]?.value;
                            if (
                                currentDate != null &&
                                typeof currentDate === "string"
                            ) {
                                return (
                                    currentDate == date ||
                                    DateTime.fromISO(currentDate).toFormat(
                                        "yyyy-MM-dd",
                                    ) == date
                                );
                            }

                            return false;
                        },
                    );
                    if (dataWithCurrentDate == null) {
                        const nullDataPoint = new Array(metadataLength).fill(
                            null,
                        );
                        nullDataPoint[dateColumnIndex] = {value: date};
                        return nullDataPoint;
                    } else {
                        const dataPointWithFormattedDate =
                            structuredClone(dataWithCurrentDate);
                        dataPointWithFormattedDate[dateColumnIndex] = {
                            value: date,
                        };
                        return dataPointWithFormattedDate;
                    }
                },
            );
            break;
        }

        case Granularity.month: {
            // This object looks like: {
            //      "01-24": "Jan 2024",
            //      "02-24": "Feb 2024"
            // }
            // Since from SQL, we get 01-24, 02-24, and for displaying we have to use readable keys
            const allMonthYearsBetweenDateRange =
                generateSqlToHumanFriendlyMonthsBetweenRange(
                    startDate,
                    endDate,
                );

            dataWithAllDates.dataPoints = Object.entries(
                allMonthYearsBetweenDateRange,
            ).map(([monthKey, monthHumanReadable]) => {
                const dataWithCurrentMonth = data.dataPoints.find(
                    (dataPoint) =>
                        dataPoint[dateColumnIndex]?.value == monthKey,
                );

                if (dataWithCurrentMonth == null) {
                    const nullDataPoint = new Array(metadataLength).fill(null);
                    nullDataPoint[dateColumnIndex] = {
                        value: monthHumanReadable,
                    };
                    return nullDataPoint;
                }

                const dataPointWithFormattedDate =
                    structuredClone(dataWithCurrentMonth);
                dataPointWithFormattedDate[dateColumnIndex] = {
                    value: monthHumanReadable,
                };
                return dataPointWithFormattedDate;
            });

            break;
        }

        case Granularity.year: {
            const years = generateYearsBetweenRange(startDate, endDate);

            dataWithAllDates.dataPoints = years.map((year) => {
                const dataWithCurrentYear = data.dataPoints.find(
                    (value) => value[dateColumnIndex]?.value == year,
                );

                if (dataWithCurrentYear == null) {
                    const nullDataPoint = new Array(metadataLength).fill(null);
                    nullDataPoint[dateColumnIndex] = year;
                    return nullDataPoint;
                }

                const dataPointWithFormattedDate =
                    structuredClone(dataWithCurrentYear);
                dataPointWithFormattedDate[dateColumnIndex] = {value: year};
                return dataPointWithFormattedDate;
            });

            break;
        }

        default: {
            const exhaustiveCheck: never = granularity;
            console.log("Unexpected granularity value! ", exhaustiveCheck);
        }
    }

    return dataWithAllDates;
}

export function generateYearsBetweenRange(
    startDate: Iso8601DateTime,
    endDate: Iso8601DateTime,
): Array<string> {
    let startDateLuxon = DateTime.fromISO(startDate, {setZone: true});
    const endDateLuxon = DateTime.fromISO(endDate, {setZone: true});

    const yearsArray: Set<string> = new Set();

    while (startDateLuxon.startOf("year") <= endDateLuxon.startOf("year")) {
        yearsArray.add(`${startDateLuxon.year}`);

        startDateLuxon = startDateLuxon.plus({year: 1});
    }

    return [...yearsArray];
}
